import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { AccountBalanceWalletOutlined, CalendarMonth, SpaceDashboard, TableRows } from "@mui/icons-material";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import type { DashboardView } from "../_hooks/useDashboardView";

type Props = {
  value: DashboardView;
  onChange: (next: DashboardView) => void;
  canViewFinance?: boolean;
  financeLabel?: string;
};

/** Dashboard presentation switch; Finance is shown only with explicit access. */
export default function DashboardViewToggle({ value, onChange, canViewFinance = false, financeLabel = "Finance" }: Props) {
  const pink = getCssVariableHandler({ variableName: "--primary-pink-hex" });
  const white = getCssVariableHandler({ variableName: "--white-rgb" });

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_e, next: DashboardView | null) => {
        if (next) onChange(next);
      }}
      aria-label="Dashboard view"
      sx={{
        border: `1px solid ${white}`,
        borderRadius: "8px",
        boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
        "& .MuiToggleButton-root": {
          color: white,
          border: "none",
          textTransform: "none",
          gap: "0.4rem",
          px: { xs: "0.6rem", sm: "0.9rem" },
          fontSize: "15px",
          "&.Mui-selected": {
            color: white,
            background: "linear-gradient(99deg, #F0F 0%, #8F00FF 100.41%)",
          },
          "&:hover": { backgroundColor: `${pink}55` },
        },
      }}
    >
      <ToggleButton value="overview" aria-label="Overview">
        <Tooltip title="Overview">
          <SpaceDashboard fontSize="small" />
        </Tooltip>
        Overview
      </ToggleButton>
      <ToggleButton value="table" aria-label="Table view">
        <Tooltip title="Table">
          <TableRows fontSize="small" />
        </Tooltip>
        Table
      </ToggleButton>
      <ToggleButton value="calendar" aria-label="Calendar view">
        <Tooltip title="Calendar">
          <CalendarMonth fontSize="small" />
        </Tooltip>
        Calendar
      </ToggleButton>
      {canViewFinance && (
        <ToggleButton value="finance" aria-label={`${financeLabel} view`}>
          <AccountBalanceWalletOutlined fontSize="small" />
          {financeLabel}
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  );
}
